<template>
    <div class="wr-page sk-dark" :class="`is-${activeTab.key}`">

        <div class="wr-header">
            <span class="wr-back" id="close" @click="viewClose('someStatus')">
                <img :src="backArrow" alt="Back" />
            </span>
            <h1>Weekly Star Rewards</h1>
            <span class="wr-headerSpacer"></span>
        </div>

        <div class="wr-inner">

            <!-- ===== Category ===== -->
            <div class="wr-tabs">
                <button v-for="tab in tabs" :key="tab.key" type="button" :class="{ 'is-on': tab.key === activeKey }"
                    @click="selectTab(tab.key)">{{ tab.label }}</button>
            </div>

            <!-- ===== Loading ===== -->
            <template v-if="loading">
                <div class="wr-card">
                    <div class="wr-skelRow" v-for="n in 6" :key="n">
                        <span class="sk"></span>
                        <span class="sk"></span>
                        <span class="sk"></span>
                    </div>
                </div>
            </template>

            <!-- ===== Failed ===== -->
            <div v-else-if="error" class="wr-card">
                <p class="wr-state">
                    {{ error }}
                    <br />
                    <button type="button" class="wr-retry" @click="fetchRewards(activeKey, true)">Try again</button>
                </p>
            </div>

            <!-- ===== Nothing configured for this category yet ===== -->
            <div v-else-if="!rewards.length" class="wr-card">
                <p class="wr-state">No rewards have been set for {{ activeTab.label }} yet.</p>
            </div>

            <template v-else>
                <!-- Whole pool, so the page opens on the biggest number -->
                <div class="wr-hero">
                    <span class="wr-cap">{{ activeTab.label }} · weekly pool</span>
                    <div class="wr-heroValue wr-num">
                        <img class="wr-coin" :src="activeTab.icon" alt="" />
                        <span>{{ formatCoins(totalPool) }}</span>
                    </div>
                    <p class="wr-heroNote">Shared by the top {{ rewards.length }} every week</p>
                </div>

                <!-- Ranks 1-3 -->
                <div class="wr-podium" v-if="podium.length">
                    <div v-for="place in podium" :key="place.rank" class="wr-place" :class="place.className">
                        <span class="wr-medal">{{ place.rank }}</span>
                        <div class="wr-placeLabel">{{ place.label }}</div>
                        <div class="wr-placeValue wr-num">
                            <img class="wr-coin" :src="activeTab.icon" alt="" />
                            <span>{{ formatCoins(place.reward_coin) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Ranks 4 and below -->
                <div class="wr-card" v-if="restRanks.length">
                    <div class="wr-cardHd">
                        <h2>All rewards</h2>
                        <span class="wr-sub">Rank {{ restRanks[0].rank }} – {{ restRanks[restRanks.length - 1].rank }}</span>
                    </div>
                    <ul class="wr-rows">
                        <li v-for="row in restRanks" :key="row.rank" class="wr-row">
                            <span class="wr-rank wr-num">{{ row.rank }}</span>
                            <span class="wr-rowLabel">Rank {{ row.rank }}</span>
                            <span class="wr-rowValue wr-num">
                                <img class="wr-coin" :src="activeTab.icon" alt="" />
                                <span>{{ formatCoins(row.reward_coin) }}</span>
                            </span>
                        </li>
                    </ul>
                </div>
            </template>

            <!-- ===== Rules ===== -->
            <div class="wr-card">
                <div class="wr-cardHd wr-ruleHd">
                    <span class="wr-ruleIcon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--wr-gold)"
                            stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M5 4.5h9.5L19 9v10.5H5z" />
                            <path d="M14 4.5V9h5" />
                            <path d="M8.5 13h7" />
                            <path d="M8.5 16.5h4.5" />
                        </svg>
                    </span>
                    <h2>{{ activeTab.label }} rules</h2>
                </div>
                <!-- The text goes in its own span: the li is a flex row, so
                     bare text nodes and <b> would each become their own flex
                     item and the sentence would break into columns. -->
                <ul class="wr-rules">
                    <li v-for="(rule, i) in activeTab.rules" :key="i">
                        <span v-html="rule"></span>
                    </li>
                </ul>
            </div>

        </div>
    </div>
</template>

<script>
import axios from 'axios';
import '@/assets/css/skeleton.css';
import './WeeklyRewardsStyle.css';
import { viewClose as androidViewClose } from '@/utils/android';
// White, not the usual black one: this page's header sits on burgundy.
import backArrow from '../../assets/images/back-whiteArrow.svg';
import beansIcon from '../../assets/images/beansIcon.png';
import diamondIcon from '../../assets/images/diamond-icon.png';

/* PLACEHOLDER RULES — swap the arrays below for the real copy when it lands.
 * Nothing else has to change: each tab renders whatever strings are here, and
 * <b> is allowed for emphasis. */
const DEMO_RULES = {
    call: [
        'Ranking is based on your <b>total call duration</b> from Monday 00:00 to Sunday 23:59 (IST).',
        // Real copy, supplied by the client — the rest of this file is still placeholder.
        'A Streamer has to attend minimum of <b>50 random calls</b> to get the reward.',
        'Rewards are credited automatically every <b>Monday</b>.',
        'Unfair or fake activity leads to disqualification.',
    ],
    charm: [
        'Ranking is based on the <b>total beans received</b> from gifts during the week.',
        // Real copy, supplied by the client.
        'Call beans and gifts sent during calls are <b>not counted</b> in charm rewards.',
        'Only <b>one account per device</b> is eligible.',
        'Rewards are credited automatically every <b>Monday</b>.',
        'Unfair or fake activity leads to disqualification.',
    ],
    rich: [
        'Ranking is based on the <b>total coins spent</b> on gifts during the week.',
        'Coins spent inside games are <b>not counted</b> towards this ranking.',
        'Only <b>one account per device</b> is eligible.',
        'Rewards are credited automatically every <b>Monday</b>.',
        'Unfair or fake activity leads to disqualification.',
    ],
};

// type is the API's own numbering: 1 rich, 2 charm, 3 call. The tabs are
// ordered the way the app presents them, which is not the same order.
//
// The currency icon follows the existing weeklyRankRewards* pages: rich pays
// in diamonds, charm and call in beans.
const TABS = [
    { key: 'call', type: 3, label: 'Top Calls', icon: beansIcon, rules: DEMO_RULES.call },
    { key: 'charm', type: 2, label: 'Top Charm', icon: beansIcon, rules: DEMO_RULES.charm },
    { key: 'rich', type: 1, label: 'Top Rich', icon: diamondIcon, rules: DEMO_RULES.rich },
];

const PLACE_META = {
    1: { className: 'is-first', label: '1st place' },
    2: { className: 'is-second', label: '2nd place' },
    3: { className: 'is-third', label: '3rd place' },
};

export default {
    name: 'WeeklyRewards',

    data() {
        return {
            backArrow,
            tabs: TABS,
            activeKey: TABS[0].key,
            // Keyed by tab so switching back to a loaded tab is instant rather
            // than showing the skeleton again.
            cache: {},
            loading: false,
            error: '',
            token: '',
        };
    },

    computed: {
        activeTab() {
            return this.tabs.find((tab) => tab.key === this.activeKey) || this.tabs[0];
        },

        rewards() {
            return this.cache[this.activeKey] || [];
        },

        podium() {
            return this.rewards
                .filter((row) => row.rank <= 3)
                .map((row) => ({ ...row, ...PLACE_META[row.rank] }));
        },

        restRanks() {
            return this.rewards.filter((row) => row.rank > 3);
        },

        totalPool() {
            return this.rewards.reduce((sum, row) => sum + (row.reward_coin || 0), 0);
        },
    },

    mounted() {
        const tokenParam = this.$route.params.token || this.$route.query.token || '';
        this.token = typeof tokenParam === 'string' ? tokenParam.replace(/^token=/, '') : tokenParam;
        this.fetchRewards(this.activeKey);
    },

    methods: {
        selectTab(key) {
            if (key === this.activeKey) return;
            this.activeKey = key;
            this.error = '';
            if (!this.cache[key]) this.fetchRewards(key);
        },

        async fetchRewards(key, force = false) {
            const tab = this.tabs.find((item) => item.key === key);
            if (!tab || (this.cache[key] && !force)) return;

            this.loading = true;
            this.error = '';

            try {
                // The endpoint is public — no Authorization header, which also
                // keeps it off the CORS preflight path.
                const response = await axios.get('api/top-rich-charm-call-list', {
                    params: { type: tab.type },
                });

                const rows = Array.isArray(response?.data?.result) ? response.data.result : [];
                // The API returns the ranks in order today, but nothing
                // guarantees it, and the podium depends on rank 1 being first.
                this.cache = {
                    ...this.cache,
                    [key]: [...rows].sort((a, b) => (a.rank || 0) - (b.rank || 0)),
                };
            } catch (err) {
                console.error('Error fetching weekly rewards', err);
                this.error = 'Could not load the rewards. Check your connection and try again.';
            } finally {
                this.loading = false;
            }
        },

        formatCoins(value) {
            return (value || 0).toLocaleString('en-IN');
        },

        // Close the WebView on back arrow click
        viewClose(status) {
            if (window.Android && typeof window.Android.viewClose === 'function') {
                androidViewClose(status);
            } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.viewClose) {
                window.webkit.messageHandlers.viewClose.postMessage('close');
            } else {
                window.parent.postMessage({ action: 'closePopup', status }, '*');
            }
        },
    },
};
</script>
